/**
 * /join/:token — 個案首次加入流程。
 *
 * 公開頁（不需 auth）：
 *   - GET /api/join/:token 預覽 session 資訊
 *   - POST /api/join/:token/register 新個案註冊 + 自動登入 + bind 到 session
 *   - POST /api/join/:token/login   已有個案登入 + bind
 *
 * 加入後直接跳到 /studio/:sessionId（Studio Lobby）— case 進去先看
 * trainer 視訊 + 三 consent toggle，trainer 按 Start 後進 live。
 * /me/sessions/:id 是給結束後回看訓練紀錄用的，不該是 join 終點。
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";

interface Preview {
  id: string;
  scheduled_for: string;
  estimated_min: number;
  state: string;
  case_name: string;
  trainer_name: string;
  join_expires_at: string;
}

export default function JoinLanding() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const { user, login, refresh } = useAuth();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [mode, setMode]       = useState<"register" | "login">("register");

  useEffect(() => {
    if (!token) return;
    api<{ ok: true; session: Preview }>(`/api/join/${token}`)
      .then(r => setPreview(r.session))
      .catch((e: any) => setError(
        e?.body?.error === "expired"      ? "這個邀請連結已過期，請聯絡訓練師重新發送。"
        : e?.body?.error === "invalid_token" ? "邀請連結無效。"
        : "無法載入 session 資訊。"
      ));
  }, [token]);

  if (error) {
    return (
      <main className="page page--auth">
        <div className="serial">JOIN · ERROR</div>
        <h1>無法加入</h1>
        <p className="muted" style={{ marginTop: "var(--s-3)" }}>{error}</p>
      </main>
    );
  }

  if (!preview) {
    return (
      <main className="page page--auth">
        <div className="muted">載入中…</div>
      </main>
    );
  }

  // Already logged in as case → just bind + go to session
  if (user?.role === "case") {
    return <BoundAsExisting token={token!} preview={preview} />;
  }

  return (
    <main className="page page--auth">
      <div className="rise rise-1" style={{ marginBottom: "var(--s-6)" }}>
        <div className="serial" style={{ marginBottom: "var(--s-3)" }}>YOU'RE INVITED</div>
        <h1 style={{ fontSize: "var(--t-2xl)" }}>
          <span className="italic-display" style={{ color: "var(--rust)" }}>{preview.trainer_name}</span> 邀你加入訓練
        </h1>
      </div>

      <div className="surface rise rise-2" style={{ padding: "var(--s-5)", marginBottom: "var(--s-6)" }}>
        <div className="kv">
          <span className="kv__key">時間</span>
          <span className="kv__val tabular">{fmtFull(preview.scheduled_for)}</span>
          <span className="kv__key">時長</span>
          <span className="kv__val tabular">{preview.estimated_min} 分鐘</span>
          <span className="kv__key">個案</span>
          <span className="kv__val">{preview.case_name}</span>
          <span className="kv__key">訓練師</span>
          <span className="kv__val">{preview.trainer_name}</span>
        </div>
        <div className="muted" style={{ fontSize: "var(--t-xs)", marginTop: "var(--s-3)", fontFamily: "var(--mono)" }}>
          邀請有效期：{preview.join_expires_at}
        </div>
      </div>

      <div className="rise rise-3">
        <div className="row" style={{ gap: "var(--s-3)", marginBottom: "var(--s-4)" }}>
          <button
            className={mode === "register" ? "btn btn--small" : "btn btn--small btn--ghost"}
            onClick={() => setMode("register")}
          >第一次來</button>
          <button
            className={mode === "login" ? "btn btn--small" : "btn btn--small btn--ghost"}
            onClick={() => setMode("login")}
          >已有帳號</button>
        </div>

        {mode === "register" && <RegisterForm token={token!}
                                              refresh={refresh}
                                              onDone={(sid) => nav(`/studio/${sid}`, { replace: true })} />}
        {mode === "login"    && <LoginForm    token={token!} login={login}
                                              onDone={(sid) => nav(`/studio/${sid}`, { replace: true })} />}
      </div>

      <p className="muted rise rise-4" style={{ marginTop: "var(--s-7)", fontSize: "var(--t-xs)" }}>
        提醒：CogniHome 不是醫療診斷或治療系統。訓練紀錄會儲存以利訓練師回顧。
      </p>
    </main>
  );
}

function BoundAsExisting({ token, preview }: { token: string; preview: Preview }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);
  async function bind() {
    setBusy(true); setErr(null);
    try {
      const r = await api<{ ok: true; session_id: string }>(`/api/join/${token}/login`, { method: "POST" });
      nav(`/studio/${r.session_id}`, { replace: true });
    } catch (e: any) {
      const code = e?.body?.error;
      setErr(
        code === "already_assigned" ? "這個 session 已綁定別的個案。請聯絡訓練師。"
        : e?.message ?? "綁定失敗"
      );
    } finally { setBusy(false); }
  }
  return (
    <main className="page page--auth">
      <div className="serial">YOU'RE INVITED · 已登入</div>
      <h1 style={{ fontSize: "var(--t-2xl)", marginTop: "var(--s-3)" }}>
        加入<span className="italic-display" style={{ color: "var(--rust)" }}>{preview.trainer_name}</span>的訓練
      </h1>
      <p className="muted" style={{ marginTop: "var(--s-3)" }}>{fmtFull(preview.scheduled_for)} · {preview.estimated_min} 分鐘</p>
      {err && <div className="alert" style={{ marginTop: "var(--s-4)" }}>{err}</div>}
      <button className="btn btn--rust" onClick={bind} disabled={busy} style={{ marginTop: "var(--s-5)" }}>
        {busy ? "加入中…" : "確認加入此 session →"}
      </button>
    </main>
  );
}

function RegisterForm({ token, refresh, onDone }: {
  token: string;
  refresh: () => Promise<void>;
  onDone: (sessionId: string) => void;
}) {
  const [form, setForm] = useState({ email: "", password: "", display_name: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const r = await api<{ ok: true; session_id: string; access_token: string; refresh_token: string }>(
        `/api/join/${token}/register`,
        { method: "POST", body: form, noAuth: true },
      );
      sessionStorage.setItem("cognihome_access_token",  r.access_token);
      sessionStorage.setItem("cognihome_refresh_token", r.refresh_token);
      // Critical: refresh AuthContext so RequireAuth sees the new user
      // before navigation; otherwise route guard kicks back to /login.
      await refresh();
      onDone(r.session_id);
    } catch (e: any) {
      const code = e?.body?.error;
      setErr(code === "email_taken" ? "這個 email 已註冊（若是你的，請改用「已有帳號」分頁）"
           : code === "weak_password" ? "密碼至少 8 個字元"
           : e?.message ?? "註冊失敗");
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="col" style={{ gap: "var(--s-3)" }}>
      <div className="field">
        <label className="field-label">姓名</label>
        <input required value={form.display_name}
               onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="例：張小明" />
      </div>
      <div className="field">
        <label className="field-label">Email</label>
        <input type="email" required value={form.email}
               onChange={e => setForm({ ...form, email: e.target.value })} />
      </div>
      <div className="field">
        <label className="field-label">設定密碼 (≥ 8 字)</label>
        <input type="password" required minLength={8} value={form.password}
               onChange={e => setForm({ ...form, password: e.target.value })} />
      </div>
      {err && <div className="alert">{err}</div>}
      <button type="submit" className="btn btn--rust" disabled={busy} style={{ justifyContent: "center", marginTop: "var(--s-2)" }}>
        {busy ? "註冊中…" : "註冊並加入訓練 →"}
      </button>
    </form>
  );
}

function LoginForm({ token, login, onDone }: {
  token: string;
  login: (e: string, p: string) => Promise<unknown>;
  onDone: (sessionId: string) => void;
}) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await login(form.email.trim(), form.password);
      const r = await api<{ ok: true; session_id: string }>(`/api/join/${token}/login`, { method: "POST" });
      onDone(r.session_id);
    } catch (e: any) {
      const code = e?.body?.error;
      setErr(code === "invalid_credentials" ? "Email 或密碼錯誤"
           : code === "already_assigned"   ? "這個 session 已綁定別的個案。"
           : e?.message ?? "登入失敗");
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="col" style={{ gap: "var(--s-3)" }}>
      <div className="field">
        <label className="field-label">Email</label>
        <input type="email" required value={form.email}
               onChange={e => setForm({ ...form, email: e.target.value })} />
      </div>
      <div className="field">
        <label className="field-label">密碼</label>
        <input type="password" required value={form.password}
               onChange={e => setForm({ ...form, password: e.target.value })} />
      </div>
      {err && <div className="alert">{err}</div>}
      <button type="submit" className="btn btn--rust" disabled={busy} style={{ justifyContent: "center", marginTop: "var(--s-2)" }}>
        {busy ? "處理中…" : "登入並加入訓練 →"}
      </button>
    </form>
  );
}

function fmtFull(s: string) {
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("zh-TW", { year: "numeric", month: "short", day: "numeric", weekday: "short", hour: "2-digit", minute: "2-digit" });
}
